/**
 * API Client for ATAM Connect backend
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class ApiClient {
    constructor() {
        this.token = localStorage.getItem('token');
    }

    setToken(token) {
        this.token = token;
        if (token) {
            localStorage.setItem('token', token);
        } else {
            localStorage.removeItem('token');
        }
    }

    async request(endpoint, options = {}) {
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }

        const response = await fetch(`${API_URL}${endpoint}`, {
            ...options,
            headers,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: 'Request failed' }));
            throw new Error(error.detail || 'Request failed');
        }

        return response.json();
    }

    // Auth
    async register(email, password, name, agreedToTerms = false) {
        const data = await this.request('/api/auth/register', {
            method: 'POST',
            body: JSON.stringify({ email, password, name, agreed_to_terms: agreedToTerms }),
        });
        this.setToken(data.access_token);
        return data;
    }

    async login(email, password) {
        const data = await this.request('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });
        this.setToken(data.access_token);
        return data;
    }

    async requestPasswordReset(email) {
        return this.request('/api/auth/request-reset', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }

    async updateProfile(name, phone, organisation) {
        return this.request('/api/auth/profile', {
            method: 'PUT',
            body: JSON.stringify({ name, phone, organisation }),
        });
    }

    async resetPassword(token, new_password) {
        return this.request('/api/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify({ token, new_password }),
        });
    }

    async getMe() {
        return this.request('/api/auth/me');
    }

    logout() {
        this.setToken(null);
    }

    // Admin User Management
    async getAllUsers() {
        return this.request('/api/admin/users');
    }

    async suspendUser(id, is_suspended) {
        return this.request(`/api/admin/users/${id}/suspend`, {
            method: 'PUT',
            body: JSON.stringify({ is_suspended })
        });
    }

    async deleteUser(id) {
        return this.request(`/api/admin/users/${id}`, {
            method: 'DELETE'
        });
    }

    // Admin Partner Management
    async getAllPartners() {
        return this.request('/api/partners');
    }

    // Excel Export Methods
    async exportEvents() {
        const response = await fetch(`${API_URL}/api/admin/export/events`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        return response.blob();
    }

    async exportUsers() {
        const response = await fetch(`${API_URL}/api/admin/export/users`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        return response.blob();
    }

    async exportContacts() {
        const response = await fetch(`${API_URL}/api/admin/export/contacts`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        return response.blob();
    }

    async exportPartners() {
        const response = await fetch(`${API_URL}/api/admin/export/partners`, {
            headers: { 'Authorization': `Bearer ${this.token}` }
        });
        return response.blob();
    }

    async importPartners(jsonData) {
        return this.request('/api/admin/partners/import', {
            method: 'POST',
            body: JSON.stringify(jsonData)
        });
    }

    async getUserStats() {
        return this.request('/api/admin/users/stats');
    }

    async createPartnerAccount({ name, email, password }) {
        return this.request('/api/admin/partner-accounts', {
            method: 'POST',
            body: JSON.stringify({ name, email, password })
        });
    }

    // Partner Event Management
    async submitPartnerEvent(eventData) {
        return this.request('/api/partner/events', {
            method: 'POST',
            body: JSON.stringify(eventData)
        });
    }

    async getMyEvents() {
        return this.request('/api/partner/my-events');
    }

    async updatePartnerEvent(id, eventData) {
        return this.request(`/api/partner/events/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(eventData)
        });
    }

    async deletePartnerEvent(id) {
        return this.request(`/api/partner/events/${id}`, {
            method: 'DELETE'
        });
    }

    // Admin Event Approval
    async getPendingEvents() {
        return this.request('/api/admin/events/pending');
    }

    async getPendingCount() {
        try {
            const data = await this.request('/api/admin/events/pending-count');
            return data.count || 0;
        } catch { return 0; }
    }

    async approveEvent(eventId) {
        return this.request(`/api/admin/events/${eventId}/approve`, {
            method: 'PUT'
        });
    }

    async rejectEvent(eventId, reason = '') {
        return this.request(`/api/admin/events/${eventId}/reject`, {
            method: 'PUT',
            body: JSON.stringify({ reason })
        });
    }

    // Events
    async getEvents(params = {}) {
        const query = new URLSearchParams(params).toString();
        return this.request(`/api/events${query ? `?${query}` : ''}`);
    }

    async getUpcomingEvents(limit = 10) {
        return this.request(`/api/events/upcoming?limit=${limit}`);
    }

    async getEvent(id) {
        return this.request(`/api/events/${id}`);
    }

    async getCategories() {
        return this.request('/api/events/categories');
    }

    // Admin Events
    async createEvent(event) {
        return this.request('/api/events/admin/events', {
            method: 'POST',
            body: JSON.stringify(event),
        });
    }

    async updateEvent(id, event) {
        return this.request(`/api/events/admin/events/${id}`, {
            method: 'PUT',
            body: JSON.stringify(event),
        });
    }

    async deleteEvent(id) {
        return this.request(`/api/events/admin/events/${id}`, {
            method: 'DELETE',
        });
    }

    // Favorites
    async getFavorites() {
        return this.request('/api/user/favorites');
    }

    async addFavorite(eventId) {
        return this.request(`/api/user/favorites/${eventId}`, {
            method: 'POST',
        });
    }

    async removeFavorite(eventId) {
        return this.request(`/api/user/favorites/${eventId}`, {
            method: 'DELETE',
        });
    }


    // Booking click tracking (replaces RSVP)
    async trackBookingClick(eventId) {
        try {
            return await this.request(`/api/events/${eventId}/booking-click`, {
                method: 'POST',
            });
        } catch (err) {
            // Non-critical — silently fail so the booking URL still opens
            console.warn('Booking click track failed:', err.message);
            return null;
        }
    }

    // Partners
    async getPartners(params = {}) {
        const query = new URLSearchParams(params).toString();
        return this.request(`/api/partners${query ? `?${query}` : ''}`);
    }

    async getPartner(id) {
        return this.request(`/api/partners/${id}`);
    }

    async getPartnerCategories() {
        return this.request('/api/partners/categories');
    }

    // Admin Partners
    async createPartner(partner) {
        return this.request('/api/partners/admin/partners', {
            method: 'POST',
            body: JSON.stringify(partner),
        });
    }

    async updatePartner(id, partner) {
        return this.request(`/api/partners/admin/partners/${id}`, {
            method: 'PUT',
            body: JSON.stringify(partner),
        });
    }

    async deletePartner(id) {
        return this.request(`/api/partners/admin/partners/${id}`, {
            method: 'DELETE',
        });
    }

    // Contact Form
    async submitContact(contactData) {
        return this.request('/api/contact', {
            method: 'POST',
            body: JSON.stringify(contactData),
        });
    }

    async getContacts(status = null) {
        const query = status ? `?status=${status}` : '';
        return this.request(`/api/contact/admin/contacts${query}`);
    }

    async getContactStats() {
        return this.request('/api/contact/admin/contacts/stats');
    }

    async updateContact(id, data) {
        return this.request(`/api/contact/admin/contacts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async updateContactStatus(id, status) {
        return this.request(`/api/contact/admin/contacts/${id}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status })
        });
    }

    async deleteContact(id) {
        return this.request(`/api/contact/admin/contacts/${id}`, {
            method: 'DELETE',
        });
    }


    async getBookingAnalytics() {
        return this.request('/api/admin/booking-analytics');
    }

    async getEventBookingStats(eventId) {
        return this.request(`/api/admin/events/${eventId}/booking-stats`);
    }

    // Banners
    async getBanners() {
        return this.request('/api/banners');
    }

    async getAllBannersAdmin() {
        return this.request('/api/banners/admin/banners');
    }

    // Banners
    async createBanner(banner) {
        return this.request('/api/banners/admin/banners', {
            method: 'POST',
            body: JSON.stringify(banner),
        });
    }

    async updateBanner(id, banner) {
        return this.request(`/api/banners/admin/banners/${id}`, {
            method: 'PUT',
            body: JSON.stringify(banner),
        });
    }

    async deleteBanner(id) {
        return this.request(`/api/banners/admin/banners/${id}`, {
            method: 'DELETE',
        });
    }

    async uploadImage(file) {
        const formData = new FormData();
        formData.append('file', file);

        // Try partner upload first (for partner users), fall back to admin upload
        const uploadUrls = [
            `${API_URL}/api/partner/upload`,
            `${API_URL}/api/admin/upload`
        ];

        let lastError = null;
        for (const url of uploadUrls) {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${this.token}` },
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                // Prefix relative upload paths with the backend base URL
                if (data.url && data.url.startsWith('/')) {
                    data.url = `${API_URL}${data.url}`;
                }
                return data;
            }
            // 403 means no access to this endpoint, try next
            if (response.status === 403) {
                lastError = new Error('No upload permission');
                continue;
            }
            // Other errors — parse and throw
            const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
            throw new Error(error.detail || 'Upload failed');
        }

        throw lastError || new Error('Upload failed');
    }

    async generateBanner(title, subtitle = '', bgColor = '#1a1a2e', textColor = '#ffffff', width = 1920, height = 600) {
        const params = new URLSearchParams({
            title,
            bg_color: bgColor,
            text_color: textColor,
            width: width.toString(),
            height: height.toString()
        });

        if (subtitle) {
            params.append('subtitle', subtitle);
        }

        return this.request(`/api/admin/generate-banner?${params.toString()}`, {
            method: 'POST'
        });
    }

    // Categories (Admin)
    async getAdminCategories() {
        return this.request('/api/admin/categories');
    }

    async createCategory(categoryData) {
        return this.request('/api/admin/categories', {
            method: 'POST',
            body: JSON.stringify(categoryData)
        });
    }

    async updateCategory(id, categoryData) {
        return this.request(`/api/admin/categories/${id}`, {
            method: 'PUT',
            body: JSON.stringify(categoryData)
        });
    }

    async deleteCategory(id) {
        return this.request(`/api/admin/categories/${id}`, {
            method: 'DELETE'
        });
    }

    // Admin Notifications
    async getAdminNotifications() {
        return this.request('/api/admin/notifications');
    }

    async markAdminNotificationRead(id) {
        return this.request(`/api/admin/notifications/${id}/read`, { method: 'PATCH' });
    }

    async markAllAdminNotificationsRead() {
        return this.request('/api/admin/notifications/read-all', { method: 'PATCH' });
    }

    // Partner Notifications
    async getPartnerNotifications() {
        return this.request('/api/partner/notifications');
    }

    async markPartnerNotificationRead(id) {
        return this.request(`/api/partner/notifications/${id}/read`, { method: 'PATCH' });
    }

    async markAllPartnerNotificationsRead() {
        return this.request('/api/partner/notifications/read-all', { method: 'PATCH' });
    }

    // Audit Log
    async getAuditLog() {
        return this.request('/api/admin/audit');
    }

    // Request edits on an event (admin)
    async requestEventEdits(eventId, note) {
        return this.request(`/api/admin/events/${eventId}/request-edits`, {
            method: 'PUT',
            body: JSON.stringify({ note })
        });
    }

    // Change password (authenticated user)
    async changePassword(new_password, current_password = null) {
        const payload = { new_password };
        if (current_password) {
            payload.current_password = current_password;
        }
        return this.request('/api/auth/change-password', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    // Resubmit partner event after edits
    async resubmitPartnerEvent(id) {
        return this.request(`/api/partner/events/${id}/resubmit`, { method: 'POST' });
    }

    // Admin — User Management
    async getAllUsers(params = {}) {
        const query = new URLSearchParams(params).toString();
        return this.request(`/api/admin/users${query ? `?${query}` : ''}`);
    }

    async getUserStats() {
        return this.request('/api/admin/users/stats');
    }

    async exportUsers() {
        const headers = {};
        if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
        const res = await fetch(`${this.baseUrl || API_URL}/api/admin/users/export`, { headers });
        if (!res.ok) throw new Error('Export failed');
        return res.blob();
    }

    async resetPartnerPassword(userId) {
        return this.request(`/api/admin/users/${userId}/reset-password`, { method: 'POST' });
    }

    // Health
    async healthCheck() {
        return this.request('/api/health');
    }
}

export const api = new ApiClient();
export default api;
